import { Address, AddressData, AddressHelper } from "@app/address/model/address";
import { ClassImporterExporter, ImportMap } from "@app/shared/resources/class-importer-exporter.service";

import { DbHelper } from "@app/shared/model/db-object.model";
import { UserData, UserHelper } from "@app/user/model/user";
import { ClareServiceSubscription } from "./clare-service-subscription";
import { ClareSystem, ClareSystemData, ClareSystemHelper } from "./clare-system";
import { SecuritySystem, SecuritySystemData } from "./security-system";

export type UserinfoKeyType = "userinfo1" | "userinfo2" | "userinfo3";

export interface Project {
    id?: number;
    addressId?: number;
    entityId?: number;
    claresystemId?: number;
    securitysystemId?: number;
    activeSubscription?: ClareServiceSubscription;
    userinfo1?: number;
    userinfo2?: number;
    userinfo3?: number;
    grandfathered?: boolean;
    address?: Address;
    claresystem?: ClareSystem;
    securitysystem?: SecuritySystem;
    notes?: string;
    accountId?: string | number;
    inactive?: boolean;
    isOccupied?: boolean;
    uuid?: string;
    name?: string;
    street?: string;
    lockoutTime?: string;
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface ProjectData {
    id: number;
    address_id?: number;
    entity_id?: number;
    claresystem_id?: number;
    securitysystem_id?: number;
    userinfo1?: number;
    userinfo2?: number;
    userinfo3?: number;
    grandfathered?: boolean;
    notes?: string;
    inactive?: boolean;
    account_id?: string | number;
    isOccupied?: boolean;
    uuid?: string;
    address?: AddressData;
    claresystem?: ClareSystemData;
    securitysystem?: SecuritySystemData;
    lockout_time?: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export class ProjectHelper extends DbHelper<Project, ProjectData> {
    static dbTypeName = "Account";
    static dbTypeNamePlural = "Accounts";

    static readonly importMap: ImportMap<Project, ProjectData> = [
        { i: "id", o: "id" },
        { i: "address_id", o: "addressId" },
        { i: "entity_id", o: "entityId" },
        { i: "claresystem_id", o: "claresystemId" },
        { i: "securitysystem_id", o: "securitysystemId" },
        { i: "userinfo1", o: "userinfo1" },
        { i: "userinfo2", o: "userinfo2" },
        { i: "userinfo3", o: "userinfo3" },
        { i: "grandfathered", o: "grandfathered" },
        { i: "account_id", o: "accountId" },
        { i: "notes", o: "notes" },
        { i: "inactive", o: "inactive" },
        { i: "isOccupied", o: "isOccupied", skipExport: true },
        { i: "uuid", o: "uuid", skipExport: true },
        { i: "address", o: "address", skipExport: true },
        { i: "claresystem", o: "claresystem", skipExport: true },
        { i: "securitysystem", o: "securitysystem", skipExport: true },
        { i: "lockout_time", o: "lockoutTime" },
    ];

    static fromDbObject(project): Project {
        return ClassImporterExporter.importObject(project, ProjectHelper.importMap);
    }

    static toDbObject(obj: Project): ProjectData {
        return ClassImporterExporter.exportObject(obj, this.importMap, false);
    }

    public address: Address;
    public claresystem: ClareSystem;
    public securitysystem: SecuritySystem;

    constructor(
        public id: number = null,
        public addressId?: number,
        public entityId?: number,
        public claresystemId?: number,
        public securitysystemId?: number,
        public activeSubscription?: ClareServiceSubscription,
        public userinfo1?: number,
        public userinfo2?: number,
        public userinfo3?: number,
        public grandfathered: boolean = false,
        public accountId?: string | number,
        public notes?: string,
        public inactive: boolean = false,
        private _uuid?: string
    ) {
        super();
    }

    fromDbObject(projectData: ProjectData): Project {
        return ProjectHelper.fromDbObject(projectData);
    }

    toDbObject(project: Project): ProjectData {
        return ProjectHelper.toDbObject(project);
    }

    get uuid(): string {
        return this._uuid;
    }
}

export class ProjectAndRelatedData {
    project: ProjectData;
    address: AddressData;
    claresystem: ClareSystemData;
    securitysystem: SecuritySystemData;
    userinfo1: UserData;

    constructor(json: Partial<ProjectAndRelatedData>) {
        this.project = json.project || null;
        this.address = json.address || null;
        this.claresystem = json.claresystem || null;
        this.securitysystem = json.securitysystem || null;
        this.userinfo1 = json.userinfo1 || null;
    }

    getParsedProject() {
        return new ProjectHelper().fromDbObject(this.project);
    }

    getParsedClareSystem() {
        return new ClareSystemHelper().fromDbObject(this.claresystem);
    }

    getParsedAddress() {
        return this.address ? AddressHelper.fromDbObject(this.address) : null;
    }

    getParsedSecuritySystem() {
        return SecuritySystem.fromDbObject(this.securitysystem || {});
    }

    getPrimaryUserInfo() {
        return new UserHelper().fromDbObject(this.userinfo1);
    }
}
